/**
 * Performance API — Custom Evals & Baseline Scores.
 *
 * Two independent dimensions, always sent explicitly:
 *
 *  source (scoring system):
 *   - `custom`   — DB-backed rubric scoring. Editable, versioned, trendable.
 *                   Supports detail and change-history endpoints.
 *   - `baseline` — Langfuse evaluator trends from completed eval runs.
 *                   Read-only. Detail and changes endpoints return 404.
 *
 *  data_source (where the traces come from):
 *   - `live` — real production traces.
 *   - `eval` — simulated / eval-run traces.
 *
 *  All four combinations are valid (custom+live, custom+eval, baseline+live, baseline+eval).
 *  Response shape is unchanged across all combinations.
 *
 * Key semantics:
 *  - `score_window` = average over the selected date range (not always 7-day).
 *  - `score_7d`     = always the trailing 7-day average (for table/detail display).
 *  - `trend_series[].key` = criterion_key (custom) or normalised evaluator key (baseline).
 *  - APIs return active rubrics only by default. Pass `active_only: false` for drafts.
 */
import apiClient, { type ApiResponse } from './client';

// ─── Shared types ────────────────────────────────────────────────────────────

export type PerformanceRange = 'today' | 'yesterday' | '7d' | '30d' | 'custom';
export type PerformanceGranularity = 'hour' | 'day';
export type PerformanceSource = 'custom' | 'baseline';
export type PerformanceDataSource = 'live' | 'eval';
export type RubricStatus = 'active' | 'draft';

export interface ScorePoint {
  ts: string;          // ISO datetime
  avg_score: number;
  sample_count: number;
}

export interface PerformanceWindow {
  start: string;       // ISO datetime
  end: string;
  granularity: PerformanceGranularity;
  range: PerformanceRange;
}

// ─── Overview ────────────────────────────────────────────────────────────────

export interface RubricTrendSeries {
  key: string;         // criterion_key — use for chart data-key and selection
  name: string;
  status: RubricStatus;
  points: ScorePoint[];
}

export interface RubricOverviewItem {
  criterion_key: string;
  name: string;
  description: string;
  status: RubricStatus;
  score_window: number | null;   // avg for the selected range
  score_7d: number | null;       // always last 7 days
  sample_count: number;
  sample_count_7d: number;
  last_modified_at: string | null;
  set_id: string;
  active_version_id: string | null;
  version_id: string;
}

export interface PerformanceOverviewResponse {
  organization_id: string;
  eval_set_name: string | null;
  range: PerformanceWindow;
  trend_series: RubricTrendSeries[];
  rubrics: RubricOverviewItem[];
}

// ─── Rubric detail ───────────────────────────────────────────────────────────

export interface RubricDetailResponse {
  organization_id: string;
  criterion_key: string;
  name: string;
  description: string;
  status: RubricStatus;
  score_window: number | null;
  score_7d: number | null;
  sample_count: number;
  sample_count_7d: number;
  last_modified_at: string | null;
  evaluation_prompt: string;
  set_id: string;
  version_id: string;
}

// ─── Rubric history ──────────────────────────────────────────────────────────

export interface RubricHistorySummary {
  current_avg: number | null;
  previous_avg: number | null;
  delta: number | null;
}

export interface RubricHistoryResponse {
  organization_id: string;
  criterion_key: string;
  range: PerformanceWindow;
  points: ScorePoint[];
  summary: RubricHistorySummary;
}

// ─── Rubric changes ──────────────────────────────────────────────────────────

export interface RubricChangeEvent {
  version_id: string;
  version_number: number;
  status: string;
  timestamp: string;
  author: string | null;
  author_user_id: string | null;
  change_summary: string;
  notes: string | null;
}

export interface RubricChangesResponse {
  organization_id: string;
  criterion_key: string;
  changes: RubricChangeEvent[];
}

// ─── Query params ────────────────────────────────────────────────────────────

export interface OverviewParams {
  range?: PerformanceRange;
  start?: string;
  end?: string;
  granularity?: PerformanceGranularity;
  set_id?: string;
  active_only?: boolean;
  source?: PerformanceSource;
  data_source?: PerformanceDataSource;
}

export interface RubricParams {
  set_id?: string;
  active_only?: boolean;
}

export interface RubricHistoryParams {
  range?: PerformanceRange;
  start?: string;
  end?: string;
  granularity?: PerformanceGranularity;
  set_id?: string;
  active_only?: boolean;
  source?: PerformanceSource;
  data_source?: PerformanceDataSource;
}

// ─── Service ─────────────────────────────────────────────────────────────────

const performanceService = {
  getOverview: (
    orgId: string,
    params?: OverviewParams,
  ): Promise<ApiResponse<PerformanceOverviewResponse>> =>
    apiClient.get<PerformanceOverviewResponse>(
      `/api/evals/performance/${orgId}/overview`,
      params,
    ),

  getRubricDetail: (
    orgId: string,
    criterionKey: string,
    params?: RubricParams,
  ): Promise<ApiResponse<RubricDetailResponse>> =>
    apiClient.get<RubricDetailResponse>(
      `/api/evals/performance/${orgId}/rubrics/${criterionKey}`,
      params,
    ),

  getRubricHistory: (
    orgId: string,
    criterionKey: string,
    params?: RubricHistoryParams,
  ): Promise<ApiResponse<RubricHistoryResponse>> =>
    apiClient.get<RubricHistoryResponse>(
      `/api/evals/performance/${orgId}/rubrics/${criterionKey}/history`,
      params,
    ),

  getRubricChanges: (
    orgId: string,
    criterionKey: string,
    params?: RubricParams,
  ): Promise<ApiResponse<RubricChangesResponse>> =>
    apiClient.get<RubricChangesResponse>(
      `/api/evals/performance/${orgId}/rubrics/${criterionKey}/changes`,
      params,
    ),
};

export default performanceService;
